import React from 'react';
import {
  Box,
  Grid,
  Skeleton,
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Visibility, 
  ThumbUp, 
  Schedule,
  VideoLibrary 
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { youtubeAPI } from '../../services/api';
import KPICard from '../charts/KPICard';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar,
  Area,
  AreaChart
} from 'recharts';
import { TextField, MenuItem } from '@mui/material';

const PerformanceTrends: React.FC = () => {
  const [days, setDays] = React.useState(30);
  // Fetch personal analytics for detailed performance data
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['youtube-personal-analytics', days],
    queryFn: () => youtubeAPI.getPersonalAnalytics(days),
    staleTime: 60000
  });

  // Fetch channel metrics for trend analysis
  const { data: channelMetrics, isLoading: channelLoading } = useQuery({
    queryKey: ['channel-metrics', days],
    queryFn: () => youtubeAPI.getChannelMetrics(days),
    staleTime: 60000
  });

  // Fetch top videos for performance comparison
  const { data: topVideos, isLoading: videosLoading } = useQuery({
    queryKey: ['top-videos', 'views', 10, days],
    queryFn: () => youtubeAPI.getTopVideos('views', 10),
    staleTime: 60000
  });

  const isLoading = analyticsLoading || channelLoading || videosLoading;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Calculate trend metrics
  const trendMetrics = React.useMemo(() => {
    if (!analytics?.personal_analytics) return null;

    const data = analytics.personal_analytics;
    const totalViews = data.reduce((sum: number, day: any) => sum + day.views, 0);
    const totalWatchTime = data.reduce((sum: number, day: any) => sum + parseFloat(day.estimated_minutes_watched), 0);
    const totalLikes = data.reduce((sum: number, day: any) => sum + day.likes, 0);
    const totalComments = data.reduce((sum: number, day: any) => sum + day.comments, 0);
    const avgViews = totalViews / data.length;
    const avgWatchTime = totalWatchTime / data.length;
    const engagementRate = totalViews > 0 ? ((totalLikes + totalComments) / totalViews) * 100 : 0;

    return {
      totalViews,
      totalWatchTime: Math.round(totalWatchTime),
      avgViews: Math.round(avgViews),
      avgWatchTime: Math.round(avgWatchTime),
      engagementRate,
      totalEngagement: totalLikes + totalComments
    };
  }, [analytics]);

  return (
    <Box>
      <SectionTitle>PERFORMANCE TRENDS &amp; ANALYSIS</SectionTitle>
      <Panel label="FILTERS" sx={{ mb: 2, p: 2 }}>
        <TextField size="small" select label="Window" value={days} onChange={e => setDays(parseInt(e.target.value, 10))}>
          {[7, 14, 30, 60, 90].map(v => <MenuItem key={v} value={v}>{v} days</MenuItem>)}
        </TextField>
      </Panel>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Total Views (30d)"
              value={trendMetrics?.totalViews || 0}
              change={0}
              format="number"
              icon={<Visibility />}
              color="#1976d2"
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Avg Daily Views"
              value={trendMetrics?.avgViews || 0}
              change={0}
              format="number"
              icon={<TrendingUp />}
              color="#4caf50"
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Watch Time (30d)"
              value={trendMetrics?.totalWatchTime || 0}
              change={0}
              format="number"
              icon={<Schedule />}
              color="#ff9800"
              subtitle="minutes"
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Engagement Rate"
              value={trendMetrics?.engagementRate || 0}
              change={0}
              format="percentage"
              icon={<ThumbUp />}
              color="#9c27b0"
            />
          )}
        </Grid>
      </Grid>

      {/* Performance Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Daily Views Trend */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="DAILY VIEWS TREND" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={analytics?.personal_analytics || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip 
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    stroke="#00ffff"
                    fill="#00ffff"
                    fillOpacity={0.15}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>

        {/* Watch Time Trend */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="WATCH TIME TREND (MIN)" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={analytics?.personal_analytics || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip 
                    formatter={(value: number) => `${formatNumber(value)} min`}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="estimated_minutes_watched"
                    stroke="#ff6600"
                    fill="#ff6600"
                    fillOpacity={0.15}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* Top Performing Videos */}
      <Panel label="TOP PERFORMING VIDEOS (LAST 7 DAYS)">
        {isLoading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topVideos?.top_videos?.slice(0, 8) || []} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={formatNumber} />
              <YAxis 
                type="category" 
                dataKey="title" 
                tick={{ fontSize: 12 }}
                tickFormatter={(title) => title.length > 30 ? `${title.substring(0, 30)}...` : title}
                width={200}
              />
              <Tooltip 
                formatter={(value: number) => [formatNumber(value), 'Views']}
                labelFormatter={(title) => `Video: ${title}`}
              />
              <Bar dataKey="view_count" fill="#4caf50" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </Box>
  );
};

export default PerformanceTrends;
